import React, { Dispatch, SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import { SnackbarProps } from '@mui/material'
import messages from '@utils/messages'

export enum SnackbarType {
  Success = 'snackbarSuccess',
  Info = 'snackbarInfo',
  Error = 'snackbarError',
  SuccessAlert = 'snackbarSuccessAlert',
  InfoAlert = 'snackbarInfoAlert',
  WarningAlert = 'snackbarWarningAlert',
  ErrorAlert = 'snackbarErrorAlert',
  AdditionalSuccessAlert = 'snackbarAdditionalSuccessAlert',
}

type MessageAndType = [string, SnackbarType]

export interface GlobalSnackbarState {
  open: SnackbarProps['open']
  message?: SnackbarProps['message']
  variant?: SnackbarType | null
  disableWindowBlurListener?: boolean
  dismissableByUser?: boolean
  actionableLink?: string
}

export enum SnackbarMessages {
  BackendError = 'backendError',
  GenericError = 'genericError',
  ItemsAddedToCart = 'itemsAddedToCart',
  resendVerificationLink = 'resendVerificationLink',
  resendEmailLink = 'resendEmailLink',
  CancelCartError = 'cancelCartError',
  CartContactInfoSaveError = 'cartContactInfoSaveError',
  CartContactPartyInfoSaveError = 'cartContactPartyInfoSaveError',
  CartCsrInstructionsSaved = 'cartCsrInstructionsSaved',
  CartCsrInstructionsError = 'cartCsrInstructionsError',
  CartErrorRequestingQuote = 'cartErrorRequestingQuote',
  CartErrorSubmitting = 'cartErrorSubmitting',
  CartErrorUpdating = 'cartErrorUpdating',
  CartSaveBillingAddressSuccess = 'cartSaveBillingAddressSuccess',
  CartSaveBillingAddressError = 'cartSaveBillingAddressError',
  CartSavePaymentInfoSuccess = 'cartSavePaymentInfoSuccess',
  CartSavePaymentInfoError = 'cartSavePaymentInfoError',
  CartShippingAddressSaveError = 'cartShippingAddressSaveError',
  CartShippingUpdateSuccess = 'cartShippingUpdateSuccess',
  CartShippingUpdateError = 'cartShippingUpdateError',
  CustomerNotAllowedToLinkProfileBefore = 'customerNotAllowedToLinkProfileBefore',
  ErrorNoRecordFoundForGivenCustomerDetails = 'errorNoRecordFoundForGivenCustomerDetails',
  NoInvoiceError = 'noInvoiceError',
  PasswordUpdateSuccess = 'passwordUpdateSuccess',
  ProfileUpdateSuccess = 'profileUpdateSuccess',
  QuoteConvertError = 'quoteConvertError',
  ListConvertToQuoteError = 'listConvertToQuoteError',
  QuickOrderConvertToQuoteError = 'quickOrderConvertToQuoteError',
  CreateQuoteError = 'createQuoteError',
  ResetPasswordError = 'resetPasswordError',
  SaveCartSuccess = 'saveCartSuccess',
  SaveCartError = 'saveCartError',
  SavedCartDeleted = 'savedCartDeleted',
  SavedOrderDeleted = 'savedOrderDeleted',
  OrderSavedSuccessfully = 'orderSavedSuccessfully',
  SubscriptionSkippedSuccessfully = 'subscriptionSkippedSuccessfully',
  SubscriptionCancelledSuccessfully = 'subscriptionCancelledSuccessfully',
  SavedCartMerged = 'savedCartMerged',
  SavedCartAddWrongType = 'savedCartAddWrongType',
  FullRegistrationValidationError = 'fullRegistrationValidationError',
  BasicRegistrationComplete = 'basicRegistrationComplete',
  FullRegistrationSaved = 'fullRegistrationSaved',
  EmailCartDoesNotExist = 'emailCartDoesNotExist',
  CartsDeleted = 'cartsDeleted',
  CartsLoaded = 'cartsLoaded',
  OciCheckoutError = 'ociCheckoutError',
  InvalidCreditCardMask = 'invalidCreditCardMask',
  OrdersApproved = 'ordersApproved',
  OrdersApprovedError = 'ordersApprovedError',
  AttributeSelectorError = 'attributeSelectorError',
  NoPDFError = 'noPDFError',
  UserNotAuthorizedOrTokenExpired = 'userNotAuthorizedOrTokenExpired',
  ShipToOrBillToPartnerNotMatched = 'shipToOrBillToPartnerNotMatched',
  GenerateQuote = 'generateQuote',
  QuoteAlreadyExistsInCart = 'quoteAlreadyExistsInCart',
  ErrorProcessingOrder = 'errorProcessingOrder',
  ErrorProcessingOrderNoContactInfo = 'errorProcessingOrderNoContactInfo',
  PaymentDEALicenseNumberEmptySnackbar = 'paymentDEALicenseNumberEmptySnackbar',
  PaymentDEALicenseNumberMaskingErrorSnackbar = 'paymentDEALicenseNumberMaskingErrorSnackbar',
  DownloadWillBeginShortly = 'downloadWillBeginShortly',
  CartMixingError = 'cartMixingError',
  SameEmproveError = 'sameEmproveError',
  B2bCheckoutError = 'b2bCheckoutError',
  B2bLogoutError = 'b2bLogoutError',
  InvalidCreditCardNumberLength = 'invalidCreditCardNumberLength',
  InvalidCreditCardExpiry = 'invalidCreditCardExpiry',
  DelegateMessageExpired = 'delegateMessageExpired',
  BasicRegistrationCompleteKorea = 'basicRegistrationCompleteKorea',
  OrderFailedCompleteRegistration = 'orderFailedCompleteRegistration',
  DigitalProductCartMixingError = 'digitalProductCartMixingError',
  SavedDocsAdded = 'savedDocsAdded',
  SavedDocDeleted = 'savedDocDeleted',
  ErrorDeletingSavedDoc = 'errorDeletingSavedDoc',
  SavedCartAddWrongTypeToPurple = 'savedCartAddWrongTypeToPurple',
  SubscribeToTrackingNotificationsSuccess = 'subscribeToTrackingNotificationsSuccess',
  UserActivityCleared = 'userActivityCleared',
  ManageOrderError = 'manageOrderError',
  ManageOrderSuccess = 'manageOrderSuccess',
  SharedCartSuccess = 'sharedCartSuccess',
}

interface SnackbarMessageFunctions {
  genericError: () => MessageAndType
  itemsAddedToCart: (itemCount: number) => MessageAndType
  resendVerificationLink: () => MessageAndType
  resendEmailLink: () => MessageAndType
  cancelCartError: () => MessageAndType
  cartContactInfoSaveError: () => MessageAndType
  cartContactPartyInfoSaveError: () => MessageAndType
  cartCsrInstructionsSaved: () => MessageAndType
  cartCsrInstructionsError: () => MessageAndType
  cartErrorRequestingQuote: (errorMessage: string) => MessageAndType
  cartErrorSubmitting: (errorMessage: string) => MessageAndType
  cartErrorUpdating: (errorMessage: string) => MessageAndType
  cartSaveBillingAddressSuccess: () => MessageAndType
  cartSaveBillingAddressError: () => MessageAndType
  cartSavePaymentInfoSuccess: () => MessageAndType
  cartSavePaymentInfoError: () => MessageAndType
  cartShippingAddressSaveError: () => MessageAndType
  cartShippingUpdateSuccess: () => MessageAndType
  cartShippingUpdateError: () => MessageAndType
  customerNotAllowedToLinkProfileBefore: (
    time: [number, number]
  ) => MessageAndType
  errorNoRecordFoundForGivenCustomerDetails: () => MessageAndType
  noInvoiceError: () => MessageAndType
  passwordUpdateSuccess: () => MessageAndType
  profileUpdateSuccess: () => MessageAndType
  quoteConvertError: (errorMessage: string) => MessageAndType
  listConvertToQuoteError: (errorMessage: string) => MessageAndType
  quickOrderConvertToQuoteError: (errorMessage: string) => MessageAndType
  resetPasswordError: (messageKey: string) => MessageAndType
  saveCartSuccess: () => MessageAndType
  saveCartError: (messageKey: string) => MessageAndType
  savedCartDeleted: () => MessageAndType
  savedCartMerged: () => MessageAndType
  savedCartAddWrongType: (productInfo: {
    productNumber: string
    companyName: string
  }) => MessageAndType
  savedDocsAdded: () => MessageAndType
  savedDocDeleted: () => MessageAndType
  savedOrderDeleted: () => MessageAndType
  errorDeletingSavedDoc: () => MessageAndType
  subscribeToTrackingNotificationsSuccess: () => MessageAndType
  fullRegistrationValidationError: () => MessageAndType
  basicRegistrationComplete: () => MessageAndType
  fullRegistrationSaved: () => MessageAndType
  emailCartDoesNotExist: () => MessageAndType
  cartsDeleted: () => MessageAndType
  cartsLoaded: () => MessageAndType
  ociCheckoutError: (errorMessage: string) => MessageAndType
  invalidCreditCardMask: () => MessageAndType
  ordersApproved: () => MessageAndType
  ordersApprovedError: () => MessageAndType
  attributeSelectorError: () => MessageAndType
  noPDFError: () => MessageAndType
  userNotAuthorizedOrTokenExpired: () => MessageAndType
  generateQuote: () => MessageAndType
  quoteAlreadyExistsInCart: () => MessageAndType
  errorProcessingOrder: (contactInfo: {
    phoneNumber: string
    email: string
  }) => MessageAndType
  errorProcessingOrderNoContactInfo: () => MessageAndType
  paymentDEALicenseNumberEmptySnackbar: (items: string) => MessageAndType
  paymentDEALicenseNumberMaskingErrorSnackbar: () => MessageAndType
  downloadWillBeginShortly: () => MessageAndType
  cartMixingError: () => MessageAndType
  sameEmproveError: () => MessageAndType
  b2bCheckoutError: (errorMessage: string) => MessageAndType
  b2bLogoutError: (errorMessage: string) => MessageAndType
  invalidCreditCardNumberLength: () => MessageAndType
  invalidCreditCardExpiry: () => MessageAndType
  delegateMessageExpired: (messageBody: string) => MessageAndType
  basicRegistrationCompleteKorea: (messageBody: string) => MessageAndType
  orderFailedCompleteRegistration: (messageBody: string) => MessageAndType
  digitalProductCartMixingError: () => MessageAndType
  savedCartAddWrongTypeToPurple: (productInfo: {
    productNumber: string
    companyNameA: string
    companyNameB: string
  }) => MessageAndType
  userActivityCleared: () => MessageAndType
  manageOrderError: (errorDetails: { phoneNumber: string }) => MessageAndType
  manageOrderSuccess: () => MessageAndType
  sharedCartSuccess: () => MessageAndType
  orderSavedSuccessfully: () => MessageAndType
  subscriptionSkippedSuccessfully: () => MessageAndType
  subscriptionCancelledSuccessfully: () => MessageAndType
}

// used for 'handle x' functions that need to type setSnackbar as an arg
export interface GlobalSnackbarContextValue {
  globalSnackbarState: GlobalSnackbarState
  setGlobalSnackbarState: Dispatch<SetStateAction<GlobalSnackbarState>>
  snackbarMessages: SnackbarMessageFunctions
}

const GlobalSnackbarContext = React.createContext<GlobalSnackbarContextValue>({
  globalSnackbarState: {
    open: false,
    message: '',
    variant: null,
    disableWindowBlurListener: false,
    dismissableByUser: false,
    actionableLink: '',
  },
  setGlobalSnackbarState: () => {
    // Added comment for avoid es-lint error
  },
  snackbarMessages: {} as SnackbarMessageFunctions,
})

export const GlobalSnackbarProvider = (props) => {
  const [globalSnackbarState, setGlobalSnackbarState] =
    React.useState<GlobalSnackbarState>({
      open: false,
      message: null,
      variant: null,
      dismissableByUser: false,
      actionableLink: '',
    })

  const intl = useIntl()
  const { formatMessage } = intl

  /*
    This list is a set of messages that are loaded into the Snackbar.
    Currently they are unordered but there is also a good amount of nested logic and instantiation that I don't believe they can simply be ordered quickly.

    Also this might not be the most efficient method to declare them.

    Core CORE-434 was created to eventually address this.
  */
  /* eslint-disable sort-keys */
  const snackbarMessages = {
    backendError: (backendErrorMsg: string): MessageAndType => [
      backendErrorMsg,
      SnackbarType.Error,
    ],
    genericError: (): MessageAndType => [
      formatMessage(messages.GENERIC_ERROR),
      SnackbarType.Error,
    ],
    itemsAddedToCart: (itemCount: number): MessageAndType => [
      `${itemCount} ${formatMessage(messages.ITEMS_ADDED_TO_CART)}`,
      SnackbarType.Success,
    ],
    resendVerificationLink: (): MessageAndType => [
      formatMessage(messages.RESEND_EMAIL_NOTIFICATION_SNACBAR),
      SnackbarType.Success,
    ],
    resendEmailLink: (): MessageAndType => [
      formatMessage(messages.RESEND_EMAIL_NOTIFICATION_SNACKBAR),
      SnackbarType.Success,
    ],
    cancelCartError: (): MessageAndType => [
      formatMessage({
        id: 'CANCEL_CART_ERROR',
        defaultMessage: 'An unknown error occurred cancelling the cart',
      }),
      SnackbarType.Error,
    ],
    cartContactInfoSaveError: (): MessageAndType => [
      formatMessage(messages.CART_CONTACT_INFO_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartContactPartyInfoSaveError: (): MessageAndType => [
      formatMessage(messages.CART_CONTACT_PARTY_INFO_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartCsrInstructionsSaved: (): MessageAndType => [
      formatMessage(messages.CART_CSR_INSTRUCTIONS_SAVED),
      SnackbarType.Success,
    ],
    cartCsrInstructionsError: (): MessageAndType => [
      formatMessage(messages.CART_CSR_INSTRUCTIONS_ERROR),
      SnackbarType.Error,
    ],
    cartErrorRequestingQuote: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_REQUESTING_QUOTE, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartErrorSubmitting: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_SUBMITTING, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartErrorUpdating: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_UPDATING, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartSaveBillingAddressSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_BILLING_ADDRESS_SUCCESS),
      SnackbarType.Success,
    ],
    cartSaveBillingAddressError: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_BILLING_ADDRESS_ERROR),
      SnackbarType.Error,
    ],
    cartSavePaymentInfoSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_PAYMENT_INFO_SUCCESS),
      SnackbarType.Success,
    ],
    cartSavePaymentInfoError: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_PAYMENT_INFO_ERROR),
      SnackbarType.Success,
    ],
    cartShippingAddressSaveError: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_ADDRESS_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartShippingUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_UPDATE_SUCCESS),
      SnackbarType.Success,
    ],
    cartShippingUpdateError: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_UPDATE_ERROR),
      SnackbarType.Error,
    ],
    customerNotAllowedToLinkProfileBefore: (
      time: [number, number]
    ): MessageAndType => {
      const [mins, secs] = time
      return [
        formatMessage(messages.CUSTOMER_NOT_ALLOWED_TO_LINK_PROFILE_BEFORE, {
          mins,
          secs,
        }),
        SnackbarType.Error,
      ]
    },
    errorNoRecordFoundForGivenCustomerDetails: (): MessageAndType => [
      formatMessage(
        messages.ERROR_NO_RECORD_FOUND_FOR_GIVEN_COMBINATION_OF_CUSTOMER_DETAILS
      ),
      SnackbarType.Error,
    ],
    noInvoiceError: (): MessageAndType => [
      formatMessage(messages.NO_INVOICE_ERROR),
      SnackbarType.ErrorAlert,
    ],
    passwordUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.PASSWORD_SUCCESSFULLY_UPDATED),
      SnackbarType.Success,
    ],
    profileUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.PROFILE_UPDATE_SUCCESS),
      SnackbarType.Success,
    ],
    quoteConvertError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    listConvertToQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.LIST_TO_QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    quickOrderConvertToQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.QUICK_ORDER_TO_QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    createQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(
        {
          id: 'CREATE_QUOTE_ERROR',
          defaultMessage: 'An error occurred while creating your quote.',
        },
        {
          errorMessage,
        }
      ),
      SnackbarType.ErrorAlert,
    ],
    resetPasswordError: (messageKey: string): MessageAndType => [
      formatMessage(messages[messageKey]),
      SnackbarType.Error,
    ],
    saveCartSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_SUCCESS),
      SnackbarType.Success,
    ],
    saveCartError: (): MessageAndType => [
      formatMessage(messages.GENERIC_ERROR),
      SnackbarType.Error,
    ],
    savedCartDeleted: (): MessageAndType => [
      formatMessage(messages.SAVED_CART_DELETED),
      SnackbarType.Success,
    ],
    savedCartMerged: (): MessageAndType => [
      formatMessage(messages.SAVED_CART_MERGED),
      SnackbarType.Success,
    ],
    savedCartAddWrongType: (productInfo: {
      productNumber: string
      companyName: string
    }): MessageAndType => [
      formatMessage(messages.SAVED_CART_ADD_WRONG_TYPE, productInfo),
      SnackbarType.ErrorAlert,
    ],
    savedDocsAdded: (): MessageAndType => [
      formatMessage({
        id: 'SAVED_MY_DOCS_ADDED',
        defaultMessage:
          'Successfully added to My Documents in the Document Library.',
      }),
      SnackbarType.Success,
    ],
    savedDocDeleted: (): MessageAndType => [
      formatMessage({
        id: 'DOCUMENTS_DELETE_SUCCESS',
        defaultMessage: 'Successfully removed documents from your library.',
      }),
      SnackbarType.Success,
    ],
    savedOrderDeleted: (): MessageAndType => [
      formatMessage({
        id: 'SAVED_ORDER_DELETED',
        defaultMessage: 'Successfully removed from saved orders.',
      }),
      SnackbarType.Success,
    ],
    errorDeletingSavedDoc: (): MessageAndType => [
      formatMessage({
        id: 'DOCUMENTS_ERROR_DELETING',
        defaultMessage: 'Error Deleting Documents.',
      }),
      SnackbarType.Error,
    ],
    subscribeToTrackingNotificationsSuccess: (): MessageAndType => [
      formatMessage({
        id: 'TRACKING_NOTIFICATION_SIGNUP_SUCCESS',
        defaultMessage:
          'You are signed up to receive email notifications for delivery updates.',
      }),
      SnackbarType.Success,
    ],
    fullRegistrationValidationError: (): MessageAndType => [
      formatMessage(messages.FULL_REGISTRATION_VALIDATION_FAILED),
      SnackbarType.Error,
    ],
    basicRegistrationComplete: (): MessageAndType => [
      formatMessage(messages.BASIC_REGISTRATION_COMPLETE),
      SnackbarType.Success,
    ],
    fullRegistrationSaved: (): MessageAndType => [
      formatMessage(messages.FULL_REGISTRATION_SAVED),
      SnackbarType.Success,
    ],
    emailCartDoesNotExist: (): MessageAndType => [
      formatMessage({
        id: 'EMAIL_CART_DOES_NOT_EXIST',
        defaultMessage: 'Email cart does not exist',
      }),
      SnackbarType.Error,
    ],
    cartsDeleted: (): MessageAndType => [
      formatMessage(messages.CARTS_DELETED),
      SnackbarType.Success,
    ],
    cartsLoaded: (): MessageAndType => [
      formatMessage(messages.CARTS_LOADED),
      SnackbarType.Success,
    ],
    ociCheckoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.OCI_CHECKOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    invalidCreditCardMask: (): MessageAndType => [
      formatMessage(messages.INVALID_MASK_FOR_CREDIT_CARD_NAME),
      SnackbarType.Error,
    ],
    ordersApproved: (): MessageAndType => [
      formatMessage(messages.ORDERS_APPROVED),
      SnackbarType.SuccessAlert,
    ],
    ordersApprovedError: (): MessageAndType => [
      formatMessage(messages.ORDERS_APPROVED_ERROR),
      SnackbarType.ErrorAlert,
    ],
    attributeSelectorError: (): MessageAndType => [
      formatMessage(messages.ATTRIBUTE_SELECTOR_ERROR),
      SnackbarType.ErrorAlert,
    ],
    noPDFError: (): MessageAndType => [
      formatMessage(messages.NO_PDF_ERROR),
      SnackbarType.ErrorAlert,
    ],
    userNotAuthorizedOrTokenExpired: (): MessageAndType => [
      formatMessage(messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED_NEW),
      SnackbarType.ErrorAlert,
    ],
    shipToOrBillToPartnerNotMatched: (): MessageAndType => [
      formatMessage(messages.SHIPTO_OR_BILLTO_PARTNER_NOT_MATCHED),
      SnackbarType.ErrorAlert,
    ],
    generateQuote: (): MessageAndType => [
      formatMessage(messages.GENERATE_QUOTE),
      SnackbarType.ErrorAlert,
    ],
    quoteAlreadyExistsInCart: (): MessageAndType => [
      formatMessage(messages.QUOTE_ALREADY_EXISTS_IN_CART),
      SnackbarType.Error,
    ],
    errorProcessingOrder: (contactInfo: {
      phoneNumber: string
      email: string
    }): MessageAndType => {
      return [
        formatMessage(messages.ERROR_PROCESSING_ORDER, contactInfo),
        SnackbarType.ErrorAlert,
      ]
    },
    errorProcessingOrderNoContactInfo: (): MessageAndType => [
      formatMessage(messages.ERROR_PROCESSING_ORDER_NO_CONTACT_INFO),
      SnackbarType.ErrorAlert,
    ],
    paymentDEALicenseNumberEmptySnackbar: (items: string): MessageAndType => [
      formatMessage(messages.PAYMENT_DEALICENSE_NUMBER_EMPTY_SNACKBAR, {
        items,
      }),
      SnackbarType.ErrorAlert,
    ],
    paymentDEALicenseNumberMaskingErrorSnackbar: (): MessageAndType => [
      formatMessage(messages.PAYMENT_DEALICENSE_NUMBER_MASKING_ERROR_SNACKBAR),
      SnackbarType.ErrorAlert,
    ],
    downloadWillBeginShortly: (): MessageAndType => [
      formatMessage(messages.DOWNLOAD_WILL_BEGIN_SHORTLY),
      SnackbarType.Success,
    ],
    cartMixingError: (): MessageAndType => [
      formatMessage(
        messages.CANNOT_CONTAIN_OTHER_PRODUCT_TYPE_WITH_EMPROVE_PRODUCTS
      ),
      SnackbarType.ErrorAlert,
    ],
    sameEmproveError: (): MessageAndType => [
      formatMessage(messages.CANNOT_CONTAIN_SAME_DOSSIER_WITH_EMPROVE_PRODUCTS),
      SnackbarType.ErrorAlert,
    ],
    b2bCheckoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.B2B_CHECKOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    b2bLogoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.B2B_LOGOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    invalidCreditCardNumberLength: (): MessageAndType => [
      formatMessage(messages.CREDIT_CARD_NUMBER_INVALID_LENGTH),
      SnackbarType.Error,
    ],
    invalidCreditCardExpiry: (): MessageAndType => [
      formatMessage(messages.CREDIT_CARD_NUMBER_INVALID_EXPIRY),
      SnackbarType.Error,
    ],
    delegateMessageExpired: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.WarningAlert,
    ],
    basicRegistrationCompleteKorea: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.AdditionalSuccessAlert,
    ],
    orderFailedCompleteRegistration: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.WarningAlert,
    ],
    digitalProductCartMixingError: (): MessageAndType => [
      formatMessage(messages.DIGITAL_PRODUCT_CART_MIXING_ERROR),
      SnackbarType.ErrorAlert,
    ],
    savedCartAddWrongTypeToPurple: (productInfo: {
      productNumber: string
      companyNameA: string
      companyNameB: string
    }): MessageAndType => [
      formatMessage(messages.SAVED_CART_ADD_WRONG_TYPE_TO_PURPLE, productInfo),
      SnackbarType.ErrorAlert,
    ],
    userActivityCleared: (): MessageAndType => [
      formatMessage({
        id: 'USER_ACTIVITY_CLEARED',
        defaultMessage: 'User activity cleared.',
      }),
      SnackbarType.Success,
    ],
    manageOrderError: (errorDetails: {
      phoneNumber: string
    }): MessageAndType => [
      formatMessage(messages.CANCEL_ORDER_ERROR_MESSAGE, errorDetails),
      SnackbarType.Error,
    ],
    manageOrderSuccess: (): MessageAndType => [
      formatMessage({
        id: 'THIS_ORDER_HAS_BEEN_CANCELLED',
        defaultMessage: 'This order has been cancelled',
      }),
      SnackbarType.Success,
    ],
    sharedCartSuccess: (): MessageAndType => [
      formatMessage({
        id: 'SHARED_CART_SUCCESS',
        defaultMessage: 'Shared cart sent!',
      }),
      SnackbarType.Success,
    ],
    orderSavedSuccessfully: (): MessageAndType => [
      formatMessage({
        id: 'ORDER_SAVED_SUCCESSFULLY',
        defaultMessage: 'Order Saved Successfully!',
      }),
      SnackbarType.Success,
    ],
    subscriptionSkippedSuccessfully: (): MessageAndType => [
      formatMessage({
        id: 'SUBSCRIPTION_SKIPPED_UCCESSFULLY',
        defaultMessage: 'Subscription Skipped Successfully!',
      }),
      SnackbarType.Success,
    ],
    subscriptionCancelledSuccessfully: (): MessageAndType => [
      formatMessage({
        id: 'SUBSCRIPTION_CANCELLED_UCCESSFULLY',
        defaultMessage: 'Subscription Cancelled Successfully!',
      }),
      SnackbarType.Success,
    ],
  }
  /* eslint-enable */

  const value = React.useMemo(
    () => ({ globalSnackbarState, setGlobalSnackbarState, snackbarMessages }),
    [globalSnackbarState, setGlobalSnackbarState, snackbarMessages]
  )

  return <GlobalSnackbarContext.Provider value={value} {...props} />
}

export interface SetSnackbar {
  (messageVariant: SnackbarMessages, messageData?: any): void
}

/**
 * Custom hook for interacting with global snackbar
 */

export const useGlobalSnackbar = () => {
  const { globalSnackbarState, setGlobalSnackbarState, snackbarMessages } =
    React.useContext(GlobalSnackbarContext)

  const setSnackbar = (
    messageVariant: string,
    messageData?: any,
    dismissableByUser?: boolean,
    actionableLink?: string
  ): void => {
    const [message, variant] = snackbarMessages[messageVariant](messageData)
    setGlobalSnackbarState({
      open: true,
      message,
      variant,
      dismissableByUser,
      actionableLink,
    })
  }
  return { globalSnackbarState, setGlobalSnackbarState, setSnackbar }
}
